import React, { useState, useRef, useEffect } from 'react';
import { ChevronDown, Globe } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';

// Shared outside-click hook.  Registers listeners on the next tick so the
// click that opens the menu does not race-close it on touch devices
// (iOS "ghost click" / double-tap problem).
function useOutsideClose(ref, isOpen, close) {
  useEffect(() => {
    if (!isOpen) return undefined;
    const handler = (e) => {
      if (ref.current && !ref.current.contains(e.target)) {
        close();
      }
    };
    // Delay registration one tick to skip the opening touch/click.
    const id = setTimeout(() => {
      document.addEventListener('mousedown', handler);
      document.addEventListener('touchstart', handler, { passive: true });
    }, 0);
    return () => {
      clearTimeout(id);
      document.removeEventListener('mousedown', handler);
      document.removeEventListener('touchstart', handler);
    };
  }, [isOpen, ref, close]);
}

// Compact language selector for header/navigation
export function LanguageSelector({ variant = 'default' }) {
  const { language, setLanguage, supportedLanguages } = useLanguage();
  const [isOpen, setIsOpen] = useState(false);
  const rootRef = useRef(null);
  useOutsideClose(rootRef, isOpen, () => setIsOpen(false));

  const currentLang = supportedLanguages.find(l => l.code === language) || supportedLanguages[0];

  const handleSelect = (langCode) => {
    setLanguage(langCode);
    setIsOpen(false);
  };

  if (variant === 'minimal') {
    return (
      <div className="relative" ref={rootRef}>
        <button
          onClick={() => setIsOpen(!isOpen)}
          className="flex items-center gap-1 px-2 py-1.5 rounded-lg hover:bg-white/10 transition-colors text-white"
          data-testid="language-selector-minimal"
        >
          <span className="text-lg">{currentLang.flag}</span>
          <ChevronDown className={`w-4 h-4 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
        </button>

        {isOpen && (
          <div className="absolute right-0 top-full mt-1 bg-white rounded-lg shadow-xl border border-gray-200 py-1 z-50 min-w-[160px]">
            {supportedLanguages.map((lang) => (
              <button
                key={lang.code}
                onClick={() => handleSelect(lang.code)}
                className={`w-full flex items-center gap-3 px-4 py-2 text-left hover:bg-blue-50 transition-colors ${
                  lang.code === language ? 'bg-blue-50 text-blue-700' : 'text-gray-700'
                }`}
                data-testid={`lang-option-${lang.code}`}
              >
                <span className="text-lg">{lang.flag}</span>
                <span className="text-sm font-medium">{lang.nativeName}</span>
              </button>
            ))}
          </div>
        )}
      </div>
    );
  }

  // Default variant - for login page
  return (
    <div className="relative" ref={rootRef}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center gap-2 px-4 py-2.5 bg-white/10 backdrop-blur-sm border border-white/20 rounded-xl hover:bg-white/20 transition-colors text-white"
        data-testid="language-selector"
      >
        <Globe className="w-4 h-4" />
        <span className="text-lg">{currentLang.flag}</span>
        <span className="text-sm font-medium">{currentLang.nativeName}</span>
        <ChevronDown className={`w-4 h-4 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
      </button>

      {isOpen && (
        <div className="absolute left-0 top-full mt-2 bg-white rounded-xl shadow-2xl border border-gray-200 py-2 z-50 min-w-[200px]">
          <p className="px-4 py-2 text-xs font-bold text-gray-500 uppercase tracking-wider">
            Select Language
          </p>
          {supportedLanguages.map((lang) => (
            <button
              key={lang.code}
              onClick={() => handleSelect(lang.code)}
              className={`w-full flex items-center gap-3 px-4 py-3 text-left hover:bg-blue-50 transition-colors ${
                lang.code === language ? 'bg-blue-50 text-blue-700' : 'text-gray-700'
              }`}
              data-testid={`lang-option-${lang.code}`}
            >
              <span className="text-xl">{lang.flag}</span>
              <div>
                <p className="text-sm font-semibold">{lang.nativeName}</p>
                <p className="text-xs text-gray-500">{lang.name}</p>
              </div>
              {lang.code === language && (
                <div className="ml-auto w-2 h-2 bg-blue-600 rounded-full" />
              )}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

// Header language selector (near avatar)
export function HeaderLanguageSelector() {
  const { language, setLanguage, supportedLanguages } = useLanguage();
  const [isOpen, setIsOpen] = useState(false);
  const rootRef = useRef(null);
  useOutsideClose(rootRef, isOpen, () => setIsOpen(false));

  const currentLang = supportedLanguages.find(l => l.code === language) || supportedLanguages[0];

  return (
    <div className="relative" ref={rootRef}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center gap-1.5 px-3 py-2 rounded-lg hover:bg-blue-50 transition-colors border border-blue-200"
        data-testid="header-language-selector"
        aria-label="Select language"
        title="Language"
      >
        <Globe className="w-4 h-4 text-blue-600" />
        <span className="text-[11px] font-bold uppercase tracking-wider text-blue-700">{currentLang.code}</span>
        <ChevronDown className={`w-4 h-4 text-blue-600 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
      </button>

      {isOpen && (
        <div className="absolute right-0 top-full mt-2 bg-white rounded-xl shadow-2xl border border-gray-200 py-2 z-50 min-w-[180px]">
          {supportedLanguages.map((lang) => (
            <button
              key={lang.code}
              onClick={() => {
                setLanguage(lang.code);
                setIsOpen(false);
              }}
              className={`w-full flex items-center gap-3 px-4 py-2.5 text-left hover:bg-blue-50 transition-colors ${
                lang.code === language ? 'bg-blue-50 text-blue-700' : 'text-gray-700'
              }`}
              data-testid={`header-lang-${lang.code}`}
            >
              <span className="text-lg">{lang.flag}</span>
              <span className="text-sm font-medium">{lang.nativeName}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

export default LanguageSelector;
